import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

export interface MyOrdineKey {
  tipork: string;
  anno: number;
  serie: string;
  numdoc: number;
}

export interface MyOrdineItem {
  riga: number;
  sku: string;
  title: string;
  quantita: number;
  quantitaEvasa: number;
  /** Stato della riga (INT-131): aperta, parziale, evasa, chiusa, annullata. */
  stato?: 'aperta' | 'parziale' | 'evasa' | 'chiusa' | 'annullata';
  /** `ec_flevas` NTS: C aperta, S chiusa. */
  flevas?: string | null;
  unmis: string;
  prezzoUnitario: number;
  totaleRiga: number;
  scont1: number;
  scont2: number;
  datCons?: string | null;
}

export interface MyOrdine {
  key: MyOrdineKey;
  id: string;
  anConto: number;
  date: string | null;
  /** Merce + spese d'incasso, IVA esclusa (INT-130). `null` se il gestionale
   *  non ha mai reso il dettaglio dell'ordine. */
  totale: number | null;
  /** `et_totdoc`, IVA inclusa: l'unico totale dell'elenco NTS, sempre presente. */
  totaleIvato: number;
  totalNet: number;
  speseSpedizione: number;
  carrier: string | null;
  paymentMethod: string | null;
  destination: string | null;
  note: string | null;
  status: 'in_lavorazione' | 'confermato' | 'evaso_parziale' | 'sospeso' | 'evaso' | 'annullato';
  flevas: string | null;
  confermato: string | null;
  sospeso: string | null;
  aperto: string | null;
  /** Ha una novità (nuovo, o stato cambiato) che l'utente non ha visto. */
  aggiornato?: boolean;
  items?: MyOrdineItem[];
}

export interface MyOrdiniResp {
  items: MyOrdine[];
  page: number;
  page_size: number;
  total: number;
  pages: number;
  /** Quanti ordini hanno una novità non vista (su tutte le pagine). */
  aggiornati?: number;
}

export function useMyOrdini(params: { page?: number; pageSize?: number; stato?: string; search?: string } = {}) {
  const { page = 1, pageSize = 50, stato, search } = params;
  const [data, setData] = useState<MyOrdiniResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('page_size', String(pageSize));
      if (stato) qs.set('stato', stato);
      if (search) qs.set('search', search);
      const { data } = await api.get<MyOrdiniResp>(`/api/clienti/me/ordini?${qs}`);
      setData(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Errore caricamento ordini');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, stato, search]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, reload: load };
}

export function useMyOrdineDetail(key: MyOrdineKey | null) {
  const [data, setData] = useState<MyOrdine | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!key) { setData(null); return; }
    setLoading(true);
    setError(null);
    const { tipork, anno, serie, numdoc } = key;
    api
      .get<MyOrdine>(`/api/clienti/me/ordini/${tipork}/${anno}/${serie}/${numdoc}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail ?? 'Errore caricamento dettaglio'))
      .finally(() => setLoading(false));
  }, [key]);

  return { data, loading, error };
}
